import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Col, Row, Spin } from 'antd';
import { useCookies } from 'react-cookie';
import VisibilitySensor from 'react-visibility-sensor';

import AccountNav from '../../components/AccountNav/AccountNav';
import AccountNavMobile from '../../components/AccountNavMobile/AccountNavMobile';
import MarketBreadcrumb from '../../components/MarketBreadcrumb/MarketBreadcrumb';
import MarketFooter from '../../components/MarketFooter/MarketFooter';
import MarketSliderCard from '../../components/MarketSliderCard/MarketSliderCard';
import NoSearchResult from '../../components/NoSearchResult/NoSearchResult';
import NotifyMeModal from '../../components/NotifyMeModal/NotifyMeModal';
import OpenPop from '../../components/OpenPop/OpenPop';
import SearchFilter from '../../components/SearchFilter/SearchFilter';
import TradeDrawer from '../PropertyListing/TradeDrawer';
import TradeModal from '../PropertyListing/TradeModal';

import { Images } from '../../config/images';

import useFilters from '../../Hooks/useFilters';
import useMediaQuery from '../../Hooks/useMediaQuery';
import useQuickBuySecondary from '../../Hooks/useQuickBuySecondary';
import useSecondaryMarketEligibility from '../../Hooks/useSecondaryMarketEligibility';
import { updateFavoriteCount } from '../../store/reducer/favoriteSlice';
import { addOrRemoveToFavouriteAction } from '../../store/reducer/initialOfferingSlice';
import { notifyMeForProperty } from '../../store/reducer/notifyMeSlice';
import {
  fetchSecondaryOfferingListAction,
  updateWebSecondaryPropertyList,
} from '../../store/reducer/secondaryOfferingSlice';
import { maxShareCalculator } from '../../utils/maxShareCalculator';
import { getUsCurrencyFormat } from '../../utils/usCurrencyFormatter';

import './MarketPlace.scss';
import { GHOST_NOTIFY, PRIMARY_BUTTON_BUY, TOOLTIP_TEXT } from '../../utils/constant';

const MarketplaceSecondary = () => {
  const [openQuickBuy, setOpenQuickBuy] = useState();
  const [cookies, setCookie] = useCookies(['secondaryHeaderMessage']);
  const [, setmobilePage] = useState(1);
  const [quickBuyPropertyData, setQuickBuyPropertyData] = useState({});
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [newsLetterChecked, setNewsLetterChecked] = useState(false);
  const [storeNotifyMeData, setStoreNotifyMeData] = useState({});
  const [notifyLoadingId, setNotifyLoadingId] = useState(0);

  const screenMD = useMediaQuery('(min-width: 1000px)');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const shouldLog = useRef(true);
  const formRef = useRef();

  const propertyListWebData = useSelector((state) => state.secondaryOffering.webData);
  const loading = useSelector((state) => state.secondaryOffering.loading);
  const loadingMore = useSelector((state) => state.secondaryOffering.loadingMore);
  const userProfileData = useSelector((state) => state.userData.userData);
  const filterApply = useSelector((state) => state.filters.filterApply);

  const quickBuySecondaryOptions = useQuickBuySecondary(setOpenQuickBuy, quickBuyPropertyData, {});
  const filterOptions = useFilters(setmobilePage);
  const { isEntityRestricted } = useSecondaryMarketEligibility();

  const isSecondaryMarketPaused = useMemo(() => {
    return propertyListWebData?.data?.isSecondaryMarketPaused === true;
  }, [propertyListWebData?.data?.isSecondaryMarketPaused]);
  
  useEffect(() => {
    updateSize();
    if (shouldLog.current) {
      shouldLog.current = false;
      featchInitialOfferingData();
    }
  }, []);

  // Entity accounts aren't supported on the Secondary Marketplace (RDS-921) —
  // bounce them back to the homepage instead of showing this page at all.
  useEffect(() => {
    if (isEntityRestricted) {
      navigate('/', { replace: true });
    }
  }, [isEntityRestricted, navigate]);
  const updateSize = () => {};
  const onFetchFail = () => {
    if (location?.state?.prevPath) {
      navigate(location.state.prevPath);
    }
    navigate(`/`);
  };
  const featchInitialOfferingData = async () => {
  // Skip entirely for Entity accounts — they're not supported on the Secondary Marketplace (RDS-921).
  if (!isEntityRestricted) {
    await dispatch(
      fetchSecondaryOfferingListAction({
        page: 1,
        perPage: 10,
        onFail: onFetchFail,
      })
    );
  }
};

  const onFavouriteFailInitialWeb = async (id) => {
    let updatedArray = propertyListWebData?.data?.properties?.map((item) =>
      item?.propertyId === id ? { ...item, likedByLoggedInUser: item?.likedByLoggedInUser } : item
    );
    const newData = {
      ...propertyListWebData,
      data: {
        ...propertyListWebData?.data,
        properties: updatedArray,
      },
    };
    await dispatch(updateWebSecondaryPropertyList({ ...newData }));
  };

  const handleOnFavoriteClickWeb = async (propertyId) => {
    if (Object.keys(userProfileData).length > 0) {
      let updatedArray = propertyListWebData?.data?.properties?.map((item) => {
        if (item?.propertyId === propertyId) {
          if (!item?.likedByLoggedInUser) {
            dispatch(updateFavoriteCount());
          }
        }
        return item?.propertyId === propertyId ? { ...item, likedByLoggedInUser: !item?.likedByLoggedInUser } : item;
      });
      const newData = {
        ...propertyListWebData,
        data: {
          ...propertyListWebData?.data,
          properties: updatedArray,
        },
      };
      await dispatch(updateWebSecondaryPropertyList({ ...newData }));
      await dispatch(addOrRemoveToFavouriteAction({ propertyId, onFail: (id) => onFavouriteFailInitialWeb(id) }));
    } else {
      navigate('/login');
    }
  };

  const handleNotifyMe = async (value) => {
    setNotifyLoadingId(value?.id);
    await dispatch(
      notifyMeForProperty(
        Object.keys(userProfileData).length
          ? {
              propertyId: value?.id,
              forSecondary: value?.isSecondary,
              onSuccess: () => {
                setOpen(false);
                setEmail('');
                setNotifyLoadingId(0);
                setNewsLetterChecked(false);
                formRef?.current?.resetFields();
              },
            }
          : {
              propertyId: storeNotifyMeData?.id,
              forSecondary: storeNotifyMeData?.isSecondary,
              signUpForNewsletter: newsLetterChecked,
              email: value.email,
              onSuccess: () => {
                setOpen(false);
                setEmail('');
                setNotifyLoadingId(0);
                setNewsLetterChecked(false);
                formRef?.current?.resetFields();
              },
            }
      )
    );
  };

  // Redirecting away — render nothing while the effect above navigates.
  if (isEntityRestricted) return null;

  return (
    <div className='marketplace marketplaceinitial'>
      {screenMD ? (
        <TradeModal
          quickBuySecondaryOptions={quickBuySecondaryOptions}
          open={openQuickBuy}
          modalState={setOpenQuickBuy}
          quickBuyPropertyData={quickBuyPropertyData}
          isSingleTab={true}
          isSell={'1'}
          featchData={() => {
            filterOptions.handleNextPage(true, 1);
            setOpenQuickBuy('');
          }}
        />
      ) : (
        <TradeDrawer
          quickBuySecondaryOptions={quickBuySecondaryOptions}
          open={openQuickBuy}
          modalState={setOpenQuickBuy}
          quickBuyPropertyData={quickBuyPropertyData}
          isSell={'1'}
          isSingleTab={true}
          featchData={() => {
            filterOptions.handleNextPage(true, 1);
            setOpenQuickBuy('');
          }}
        />
      )}
      <div className='sticky-nav' id="account-nav-wrap">
        <AccountNav />
        <AccountNavMobile />
      </div>
      <Spin spinning={loading || loadingMore} wrapperClassName={(loading || loadingMore) && 'ant-spin--fullscreen'} className={(loading || loadingMore) && 'ant-spin--fullscreen'}>
        <MarketBreadcrumb
          filterApply={filterApply}
          propertyListWebData={propertyListWebData}
          title='Invest'
          market='Realbricks Market'
          link='/marketplace/initial'
          linkText='Explore initial offerings'
        />
        {!(!filterApply && propertyListWebData?.data?.count <= 0) && (
          <SearchFilter
            page='Realbricks Market'
            totalProperties={propertyListWebData?.data?.count ?? 0}
            apiIsSecondary={true}
            filterOptions={filterOptions}
            // handleApplyFilter={handleApplyFilter}
          />
        )}
        {cookies.secondaryHeaderMessage !== 'true' && (
          <OpenPop
            title='Secondary Market'
            description='A peer-to-peer marketplace where investors can buy and sell shares of individual properties, subject to availability and platform rules.'
            art={Images.openpopsecondary}
            onClose={() =>
              setCookie('secondaryHeaderMessage', true, {
                path: '/marketplace/secondary',
              })
            }
          />
        )}
        <div className='initial-offer-wrapper'>
          {propertyListWebData?.data?.properties?.length > 0 && (
            <div className='container-fluid'>
              <Row gutter={[32, 32]}>
                {propertyListWebData?.data?.properties?.map((item) => (
                  <Col key={item.id} xs={24} sm={12} md={8}>
                    <MarketSliderCard
                      id={item?.propertyId}
                      label={item?.label}
                      multiFamilyTotalUnit={item?.multiFamilyTotalUnit}
                      isSecondary={true}
                      MarketBlockHead={item?.propertyName}
                      images={item?.images && Object.values(item?.images)}
                      Bids={item?.bids}
                      Asks={item?.asks}
                      LastPriceLabel='Last price'
                      LastPrice={`$ ${getUsCurrencyFormat(item?.lastTradedPrice)}`}
                      ShareDivLable='Est. Dividend Yield'
                      ShareDivValue={item?.dividendYield ?? 'NA'}
                      isFavorite={item?.likedByLoggedInUser}
                      isSecondaryMarketPaused={isSecondaryMarketPaused}
                      ShareDivTooltip={TOOLTIP_TEXT.ESTIMATED_DIVIDEND_YIELD}
                      NotAvailableTooltip='The Activity shows the number of bids and ask done for the property. Currently there is no data available for bids and asks.'
                      PrimaryButtonBuy={PRIMARY_BUTTON_BUY.includes(item?.label) && 'Buy now'}
                      isBuyDisabled={
                        item?.ownedEquity >= 9.8 || maxShareCalculator(item?.totalShare, item?.requestedShares, item?.ownedEquity) < 1
                      }
                      GhostNotify={GHOST_NOTIFY.includes(item?.label) && 'Notify me'}
                      LocationProperty={`${item?.cityName}, ${item?.stateName}`}
                      RentFamily={item?.propertyType}
                      notifyLoadingId={notifyLoadingId}
                      BtnClick={() => {
                        setQuickBuyPropertyData(item);
                        quickBuySecondaryOptions.openForBuy();
                      }}
                      notifyMeBtnClick={(value) => {
                        Object.keys(userProfileData).length ? handleNotifyMe(value) : setStoreNotifyMeData(value) || setOpen(true);
                      }}
                      propertyType={item?.propertyType}
                      propertyTypeImage={item?.propertyTypeImage}
                      onFavoriteClick={() => handleOnFavoriteClickWeb(item?.propertyId)}
                    />
                  </Col>
                ))}
              </Row>
              <VisibilitySensor
                onChange={(value) => {
                  filterOptions?.handleInfiniteScrollWeb(value, propertyListWebData, true);
                }}
              >
                <div className='agreement__modal__dot'>.</div>
              </VisibilitySensor>
            </div>
          )}
        </div>
        <MarketFooter />
      </Spin>
      {propertyListWebData?.data?.properties?.length <= 0 && (
        <NoSearchResult
          isSecondary={true}
          title='No properties match your filter or search criteria.'
          description='Try adjusting your filters or expanding your search to see more results.'
          filterText='Try removing some other filters to see more results.'
          handleRemoveLeverage={() => filterOptions?.handleRemoveLeverage(true)}
          handleRemoveLocationFilter={() => filterOptions?.handleRemoveLocationFilter(true)}
          handleClearAll={() => filterOptions?.handleResetAllFilters(true)}
        />
      )}
      <NotifyMeModal
        open={open}
        setOpen={setOpen}
        email={email}
        setEmail={setEmail}
        newsLetterChecked={newsLetterChecked}
        setNewsLetterChecked={setNewsLetterChecked}
        handleNotifyMe={handleNotifyMe}
        ref={formRef}
      />
    </div>
  );
};

export default MarketplaceSecondary;
